import React, { useEffect, useState } from "react";
import { makeStyles, Typography, useTheme } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import Grid from '@material-ui/core/Grid';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Slider from "react-slick";
import { pwaSettings } from 'configs/pwaConstants';
import CardContent from '@material-ui/core/CardContent';
import LAOChartCard from './LAO_Common/LAOChartCard';
import moment from "moment";

export const useStylesLeadtime = makeStyles((theme) => ({
    rootClassLeadtime: {
        height: "auto",
        minHeight: theme.spacing(25),
        borderRadius: theme.spacing(1),
        background: theme.palette.card.default,
    },
    titleClassLeadtime: {
        marginRight: "auto",
        paddingBottom: theme.spacing(2),
        fontSize: theme.spacing(2),
        textTransform: "none",
        [theme.breakpoints.down('sm')]: {
            paddingBottom: 0,
        }
    },
    sliderClassLeadTime: {
        margin: `0px ${theme.spacing(2.25)}px ${theme.spacing(1)}px`,
        "& .slick-current": {
            display: "flex",
            justifyContent: "center"
        }
    },
}));

const LaoLeadTimeKpiWrapper = ({ defaultFilters }) => {
    const classesLeadtime = useStylesLeadtime();
    const { t } = useTranslation();
    const isMobileLeadTime = useMediaQuery(useTheme().breakpoints.down('sm'));
    const [innerWidth, setInnerWidth] = useState(() => window.innerWidth - window.innerWidth / 10 * 1.5)

    useEffect(() => {
        const handleResize = () => setInnerWidth(() => window.innerWidth - window.innerWidth / 10 * 1.5);
        window.addEventListener('resize', handleResize);
        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    let chartsLeadTime = [
        {
            title: t("lead_time_by_month_and_year"),
            subtype: "LEAD_TIME_ORIGIN_BY_MONTH",
            subtype1: "LEAD_TIME_INTERNATIONAL_BY_MONTH",
            subtype2: "LEAD_TIME_DESTINATION_BY_MONTH",
            subtype3: "LEAD_TIME_TOTAL_BY_MONTH",
            chart: "grouped",
            page: "leadTimeKPI",
            height: 400,
            minWidth: innerWidth,
            maxWidth: innerWidth,
            keys: ['previous', 'current'],
            stackedKeys: ["origin", "international", "destination"],
            stackGroups: {
                previous: ["previous_origin", "previous_international", "previous_dest"],
                current: ["current_origin", "current_international", "current_dest"]
            },
            stackTotal: {
                previous: "previous_total",
                current: "current_total"
            },
            getGroupLabel: (group, d) =>
                group === "previous"
                    ? moment(d.previousStartDate).format("MMM-YY")
                    : moment(d.currentStartDate).format("MMM-YY"),
            barColors: 'set24',
            xKey: "formattedStartDate",
            cursorType: `default`,
            showLegend: true,
            showBarLabel: !isMobileLeadTime,
            showXAxisLabel: !isMobileLeadTime,
            text1: isMobileLeadTime ? "org" : "origin",
            text2: isMobileLeadTime ? "intl" : "international",
            text3: isMobileLeadTime ? "destMobile" : "destination",
            text4: isMobileLeadTime ? "tot" : "total",
            text5: "min",
            text6: "max",
            text7: "avg",
            radioKey: "origin",
            radioKey1: "international",
            radioKey2: "destination",
            radioKey3: "total",
            radioKey4: "min",
            radioKey5: "max",
            radioKey6: "avg",
            testId: "lead-time-by-month-and-year-chart",
        },
        {
            title: 'lead_time_by_origin_country',
            subtype: 'LEAD_TIME_BY_COUNTRY_QUARTER',
            chart: 'table',
            page: 'leadTimeKPI',
            height: 400,
            minWidth: innerWidth,
            maxWidth: innerWidth,
            columnsKey: 'years',
            subcolumns: ['Q1', 'Q2', 'Q3', 'Q4'],
            categories: [
                { label: 'origin', valueKey: 'avgOrigin' },
                { label: 'international', valueKey: 'avgInternational' },
                { label: 'destination', valueKey: 'avgDestination' },
                { label: 'total', valueKey: 'avgTotal' },
            ],
            fixedColumns: [
                { key: 'originCountry', label: 'origin_country' },
                { key: 'destinationCountry', label: 'destination_country' },
                { key: 'customer', label: 'customer' },
                { key: 'transitionType', label: '' }
            ],
            columnPinningMode: 'fixed',
            stickyColumns: 4,
            hideGridLines: true,
            groupByKeys: ['originCountry', 'destinationCountry', 'customer.name'],
            testId: 'lead-time-by-lane-table-kpi',
        },
        {
            title: t("avg_international_transit_by_shipper"),
            subtype: "LEAD_TIME_BY_SHIPPER",
            chart: "matrix",
            page: "leadTimeKPI",
            height: 400,
            minWidth: innerWidth,
            maxWidth: innerWidth,
            rowKey:"name",
            getNestedData: (row) => row.metrics,
            testId: "avg-international-transit-chart",
        }
    ];
    
    return (
        <Card className={classesLeadtime.rootClassLeadtime} data-testid="lao-leadtime-kpis-wrapper">
            <CardHeader
                title={<Typography className={classesLeadtime.titleClassLeadtime} variant="h3" component={"h3"}>{t("live_status_text").toUpperCase()}</Typography>}
            />
            {
                isMobileLeadTime && <>
                    <Slider {...pwaSettings} className={classesLeadtime.sliderClassLeadTime}>
                        {
                            chartsLeadTime.map(d => {
                                return (
                                    <div>
                                        <LAOChartCard {...d} maxWidth={innerWidth} minWidth={innerWidth} boxShadow="none" defaultFilters={defaultFilters} />
                                    </div>
                                )
                            })
                        }
                    </Slider>
                </>
            }
            {
                !isMobileLeadTime && <>
                    <CardContent p={0}>
                        <Grid
                            container
                            spacing={3}
                            alignItems="center"
                            justifyContent="center"
                        >
                            {
                                chartsLeadTime.map(d => {
                                    return (
                                        <Grid item p={1}>
                                            <LAOChartCard {...d} maxWidth={innerWidth} minWidth={innerWidth} defaultFilters={defaultFilters} />
                                        </Grid>
                                    )
                                })
                            }
                        </Grid>
                    </CardContent>
                </>
            }
        </Card>
    );

}

export default LaoLeadTimeKpiWrapper;